/** @format */
import {add, differenceInDays, format} from 'date-fns';
import {ru} from 'date-fns/locale';
import {t} from 'i18next';

const formatDate = (date: string | Date): string => {
  return format(date, 'd MMMM', {locale: ru});
};

export interface ReturnHeaderDate {
  dateToDisplay: string;
  periodDays?: string;
}

interface Options {
  currentDate: string;
  startDate: string;
  previousDate?: string;
  isDiscreteness: boolean;
}

/**
 * Вывод даты:
 * 1. Если она единственная: 14 ноября
 * 2. Если период: 10 ноября — 16 ноября. 7 дней.
 *
 * Варианты:
 * - previousDate есть:
 *   * Если разница < 2 дней — показываем только currentDate
 *   * Иначе период: (previousDate + 1 день) — currentDate
 * - previousDate нет:
 *   * Если startDate >= currentDate — показываем только currentDate
 *   * Если startDate < currentDate — период: startDate — currentDate
 */

const getHeaderDate = ({
  currentDate,
  startDate,
  previousDate,
  isDiscreteness,
}: Options): ReturnHeaderDate => {
  // В обычном режиме просто возвращаем дату
  if (!isDiscreteness) {
    return {
      dateToDisplay: formatDate(currentDate),
    };
  }

  // Случай, когда предыдущей даты нет
  if (!previousDate) {
    const diffWithStart = differenceInDays(currentDate, startDate);

    // Если старт не раньше текущей даты — просто показываем текущую
    if (diffWithStart <= 0) {
      return {
        dateToDisplay: formatDate(currentDate),
      };
    }

    // Период: с startDate по currentDate (startDate включительно)
    const startDateString = formatDate(startDate);
    const endDateString = formatDate(currentDate);

    return {
      dateToDisplay: `${startDateString} — ${endDateString}`,
      periodDays: t('plurals.days', {count: diffWithStart + 1}),
    };
  }

  // Случай, когда previousDate есть
  const diffWithPrevious = differenceInDays(currentDate, previousDate);

  if (diffWithPrevious < 2) {
    return {
      dateToDisplay: formatDate(currentDate),
    };
  }

  const periodStart = add(previousDate, {days: 1});
  const startDateString = formatDate(periodStart);
  const endDateString = formatDate(currentDate);

  return {
    dateToDisplay: `${startDateString} — ${endDateString}`,
    periodDays: t('plurals.days', {count: diffWithPrevious}),
  };
};

export {getHeaderDate};
